import * as yup from "yup";

const parseCprDate = (value) => {
    if (!value) return null;

    value = value.replace(/\s/g, '');

    if (!/^\d{10}$/.test(value)) {
        return null;
    }

    const day = parseInt(value.slice(0, 2), 10);
    const month = parseInt(value.slice(2, 4), 10);
    const year = parseInt(value.slice(4, 6), 10);
    const seventhDigit = parseInt(value.charAt(6), 10);

    let century;
    if (seventhDigit >= 0 && seventhDigit <= 3) {
        century = 1900;
    } else if (seventhDigit === 4 || seventhDigit === 9) {
        // For digits 4 and 9: only valid for years 00-36 (2000-2036)
        if (year <= 36) {
            century = 2000;
        } else {
            // Years 37-99 with digit 4 or 9 are invalid
            return null;
        }
    } else if (seventhDigit >= 5 && seventhDigit <= 8) {
        // For digits 5-8: only valid for years 00-57 (2000-2057) or 58-99 (1858-1899)
        if (year <= 57) {
            century = 2000;
        } else {
            // Years 58-99 map to 1858-1899
            century = 1800;
        }
    } else {
        return null;
    }

    const fullYear = century + year;
    const birthDate = new Date(fullYear, month - 1, day);

    if (birthDate.getDate() !== day ||
        birthDate.getMonth() !== month - 1 ||
        birthDate.getFullYear() !== fullYear) {
        return null;
    }

    // CPR numbers cannot be in the future
    if (birthDate > new Date()) {
        return null;
    }
    return birthDate;
};

export const getYupValidationSchema = (validators, isValueAnArray) => {
    let schema = isValueAnArray ? yup.array() : yup.string();
    validators.forEach((validator) => {
        if (validator.type === "RequiredValidator" && !isValueAnArray) {
            schema = schema.required(validator.model.message);
        }

        if (validator.type === "RequiredValidator" && isValueAnArray) {
            schema = schema
                .min(1, validator.model.message);
        }

        if (validator.type === "RegularExpressionValidator") {
            schema = schema.matches(new RegExp(validator.model.jsPattern), validator.model.message);
        }

        if (validator.type === "FieldMatchValidator") {
            schema = schema.oneOf([yup.ref(validator.model.matchField)], validator.model.message);
        }

        if (validator.type === "EmailFormatValidator") {
            schema = schema.email(validator.model.message || 'Ugyldig e-mail format');
        }

        if (validator.type === "EmailDomainValidator") {
            schema = schema.test(
                "valid-email-domain",
                validator.model.message || "Ugyldig e-mail domain",
                (value) => {
                    if (!value) return true;
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) return false;

                    if (!Array.isArray(validator.model.validDomains) || validator.model.validDomains.length === 0) return true;

                    const domain = value.split("@")[1];
                    return validator.model.validDomains.includes(domain);
                }
            );
        }

        if (validator.type === "MaxFileSizeValidator") {
            schema = schema.test(
                "valid-total-file-size",
                validator.model.message,
                (files) => {
                    if (!files || files.length === 0) return true;
                    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
                    return totalSize <= validator.model.sizeInBytes;
                }
            );
        }

        if (validator.type === "PhoneCountryCodeValidator") {
            schema = schema.test(
                "valid-phone-country-code",
                validator.model.message || "Invalid phone country code",
                (value) => {
                    if (!value) return true;
                    const phoneCountryCodeRegex = /^\+\d{1,5}$/;
                    return phoneCountryCodeRegex.test(value);
                }
            );
        }

        if (validator.type === "CprValidator") {
            schema = schema.test(
                "valid-cpr",
                validator.model.message || "CPR-nr. er ikke validt. Det skal bestå af 10 tal ddmmåå-xxxx",
                function (value) {
                    const birthDate = parseCprDate(value);
                    return birthDate !== null;
                }
            );
        }

        if (validator.type === "CprAgeValidator") {
            schema = schema.test(
                "valid-cpr-age",
                validator.model.message || "CPR-nr. er ikke validt. Det skal bestå af 10 tal ddmmåå-xxxx",
                function (value) {
                    const birthDate = parseCprDate(value);
                    if (birthDate === null) {
                        return false;
                    }

                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                        age--;
                    }

                    const { minAge, maxAge, tooYoungMessage, tooOldMessage } = validator.model;

                    if (minAge !== undefined && age < minAge) {
                        return this.createError({ message: tooYoungMessage || `Du skal være mindst ${minAge} år gammel` });
                    }

                    if (maxAge !== undefined && maxAge !== 0 && age > maxAge) {
                        return this.createError({ message: tooOldMessage || `Du må højst være ${maxAge} år gammel` });
                    }

                    return true;
                }
            );
        }
        // other validation types
    });

    return schema;
};